// A small observable base for objects that keep state and tell others when
// it changes. A subclass owns its state and changes it only through
// `update`; readers either take the whole state with `subscribe` or one
// slice of it with `select`, which calls back only when that slice changes.
// The state object is replaced on every update, never mutated, so a reader
// may keep the one it was given.

export type Listener<S> = (state: S) => void;

export class Observable<S extends object> {
  private current: S;
  private readonly listeners = new Set<Listener<S>>();

  constructor(initial: S) {
    this.current = initial;
  }

  /** The state now. */
  state(): S {
    return this.current;
  }

  /** Called with every change; returns the unsubscribe. */
  subscribe(listener: Listener<S>): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  /**
   * Follow one slice of the state: `listener` is called with the slice when
   * it changes, compared with `equals` (identity by default). Returns the
   * unsubscribe. The listener is not called for the current value.
   */
  select<T>(selector: (state: S) => T, listener: (slice: T) => void, equals: (a: T, b: T) => boolean = Object.is): () => void {
    let last = selector(this.current);
    return this.subscribe((state) => {
      const next = selector(state);
      if (equals(last, next)) return;
      last = next;
      listener(next);
    });
  }

  /** Replace part of the state and tell every listener. */
  protected update(patch: Partial<S>): void {
    this.replace({ ...this.current, ...patch });
  }

  /** Replace the whole state and tell every listener. */
  protected replace(state: S): void {
    this.current = state;
    for (const listener of this.listeners) listener(this.current);
  }
}
